<template>
  <div class="c-member-list">
    <v-container grid-list-xs text-xs-left pa0>
      <v-layout row wrap>
        <v-flex xs12 align-end class="c-m-bc le-tbs-hide">
          <div class="c-fl">
            <v-breadcrumbs divider="/" :items="breadcrumbs">
              <template slot="item" slot-scope="props">
                {{ props.item.text }}
              </template>
            </v-breadcrumbs>
          </div>
          <div class="c-fr">
            <balloon
              v-if="requireAddMemberHelp"
              :msg="$T('help.msg.invite')"
              top="17px"
              right="28px"
              pos="top_right"
            ></balloon>
          </div>
          <div class="c-fr">
            <v-btn
              class="c-invite-btn"
              :to="{ name: 'memberInvite', params: { id: selectedCommunity.id } }"
              round
              color="clr-primary"
              dark
              depressed
              small
              ><v-icon>mdi-account-plus</v-icon>{{ $T('page.member.invite.button') }}</v-btn
            >
          </div>
          <div class="c-fr">
            <v-btn
              class="c-invite-btn"
              :disabled="isGuest"
              @click="onCreateGroupClicked()"
              round
              color="clr-primary"
              dark
              :outline="!leTbS"
              :small="!leTbS"
              :class="{ 'elevation-12': leTbS }"
              ><v-icon>mdi-account-multiple-plus</v-icon
              >{{ $T('page.member.add-group.button') }}</v-btn
            >
          </div>
        </v-flex>

        <v-flex xs12 class="c-header-space"> </v-flex>
        <v-flex xs12>
          <v-list two-line class="c-main-shadow">
            <v-subheader class="c-top-search">
              <div class="c-subheader first">
                <div class="l c-m-search c-flex">
                  <div v-if="cid != 0" class="c-top-chip">
                    <v-chip small close v-model="cid_chip">
                      <v-avatar size="20" class="c-small-avatar">
                        <c-img :src="getCommunityCoverPath(cid)" />
                      </v-avatar>
                      <span class="c-chip" :class="{ ss: leTbS, sm: !leTbS }">{{
                        getCommunityName(cid)
                      }}</span>
                    </v-chip>
                  </div>
                  <v-text-field
                    v-model="search"
                    append-icon="search"
                    :placeholder="$T('common.search')"
                    color="clr-primary"
                    class="n-p"
                  ></v-text-field>
                  <div class="c-tip-btn">
                    <v-tooltip :top="!leTbS" :bottom="leTbS">
                      <v-btn
                        slot="activator"
                        outline
                        fab
                        small
                        dark
                        class="ss"
                        :color="includeInactive ? 'primary' : 'grey'"
                        @click="includeInactive = !includeInactive"
                      >
                        <v-icon>mdi-account-off</v-icon>
                      </v-btn>
                      <span>{{ $T('page.member.list.include.inactive') }}</span>
                    </v-tooltip>
                  </div>
                </div>
              </div>
            </v-subheader>
            <v-subheader class="c-top-list-space le-tbs-show"> </v-subheader>
            <v-subheader>
              <div class="c-subheader">
                <div class="l">{{ $T('attributes.groups') }}</div>
                <div class="r">
                  <v-btn
                    class="c-sub-btn c-sub-add-group-btn le-tbs-show"
                    @click="onCreateGroupClicked()"
                    color="clr-btn"
                    flat
                    small
                    ><v-icon>mdi-account-multiple-plus</v-icon
                    >{{ $T('page.member.add-group.button') }}</v-btn
                  >
                </div>
              </div>
            </v-subheader>

            <template v-for="(item, index) in groupList">
              <v-divider v-if="index > 0" :inset="true" :key="`g${index}`"></v-divider>

              <v-list-tile avatar :class="'c-click c-click-g-' + item.id" @click="onRecordClicked">
                <v-list-tile-avatar class="c-cover-40">
                  <c-img :src="`/cover/g/${item.id}/${item.active_time}/`" />
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-title>{{ item.name }}</v-list-tile-title>
                  <v-list-tile-sub-title>{{ item.count }}</v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action v-if="showChip">
                  <community-chip-s
                    :onlyicon="leTbS"
                    :cid="item.community_id"
                    :class="'c-hand c-click c-click-c-' + item.community_id"
                  />
                </v-list-tile-action>
              </v-list-tile>
            </template>

            <tail-loader
              v-if="filteredGroups"
              :srcs="filteredGroups"
              :unit="10"
              v-model="groupList"
            />
            <div v-if="filteredGroups.length == 0" class="c-nodata">
              {{ $T('common.no_data') }}
            </div>

            <v-divider></v-divider>

            <v-subheader>
              <div class="c-subheader">
                <div class="l">{{ $T('attributes.members') }}</div>
                <div class="r">
                  <v-btn
                    class="c-sub-btn c-sub-member-invite-btn le-tbs-show"
                    :to="{ name: 'memberInvite', params: { id: selectedCommunity.id } }"
                    color="clr-btn"
                    flat
                    small
                    ><v-icon size="30">mdi-account-plus</v-icon
                    >{{ $T('page.member.invite.button') }}</v-btn
                  >
                </div>
              </div>
            </v-subheader>

            <template v-for="(item, index) in memberList">
              <v-divider v-if="index > 0" :inset="true" :key="`m${index}`"></v-divider>

              <v-list-tile
                avatar
                :class="'c-click c-click-m-' + item.id"
                @click="onRecordClicked"
                class="c-m-r"
              >
                <div class="c-self-mark" v-if="isUserSelf(item.community_id, item.id)">
                  <v-icon small color="amber">mdi-bookmark</v-icon>
                </div>

                <v-list-tile-avatar class="c-cover-40">
                  <c-img :src="`/cover/cm/${item.id}/${item.cover_changed_at}/`" />
                </v-list-tile-avatar>

                <v-list-tile-content>
                  <v-list-tile-title>{{ getCmName(item) }}</v-list-tile-title>
                  <v-list-tile-sub-title>
                    <div class="c-sub-title">
                      <!-- issue#30: タグは名前の下・左端。役割等はタグの右に表示 -->
                      <span v-if="item.tag" class="c-tag-mini"
                        ><v-icon size="11">mdi-tag</v-icon>{{ item.tag }}</span
                      >
                      <template v-if="item.is_friend">
                        <span
                          v-if="!$utils.isFriendStatusActive(item.friend_status)"
                          class="f-danger"
                          >{{ $utils.friendStatus(item.friend_status) }}</span
                        >
                      </template>
                      <template v-else>
                        <span v-if="item.status == 1">{{ $utils.role(item.role) }}</span>
                        <span v-else class="f-danger">{{
                          $utils.status(item.status, item.role)
                        }}</span>
                      </template>
                    </div>
                  </v-list-tile-sub-title>
                </v-list-tile-content>

                <v-list-tile-action v-if="showChip">
                  <community-chip-s
                    :onlyicon="leTbS"
                    :cid="item.community_id"
                    :class="'c-hand c-click c-click-c-' + item.community_id"
                  />
                </v-list-tile-action>
              </v-list-tile>
            </template>

            <tail-loader
              v-if="filteredMembers"
              :srcs="filteredMembers"
              :unit="10"
              v-model="memberList"
            />
            <div v-if="filteredMembers.length == 0" class="c-nodata">
              {{ $T('common.no_data') }}
            </div>
          </v-list>
        </v-flex>
      </v-layout>

      <v-btn
        class="le-tbs-show c-member-invite-btn elevation-12"
        :to="{ name: 'memberInvite', params: { id: selectedCommunity.id } }"
        round
        color="clr-btn"
        dark
        depressed
        ><v-icon>mdi-account-plus</v-icon>{{ $T('page.member.invite.button') }}</v-btn
      >

      <member-info-dialog
        ref="memberInfoDialog"
        @changeValues="changeValues"
        @profileChanged="onProfileChanged"
        @tagChanged="onTagChanged"
      />

      <group-info-dialog
        ref="groupInfoDialog"
        @changed="onGroupChanged"
        @deleted="onGroupDeleted"
      />

      <edit-group-dialog ref="editGroupDialog" @changed="onGroupChanged" />
    </v-container>
  </div>
</template>

<script>
import { COMMIN } from '../../constant'
import UTILS from '../../utils/utils'
import { communitySelect } from '../mixins/CommunitySelect'
import { getInactiveMembers } from '../../api/communityMember'
import memberInfoDialog from './MemberInfoDialog.vue'
import groupInfoDialog from './GroupInfoDialog.vue'
import editGroupDialog from './EditGroupDialog'

export default {
  props: ['communityId'],
  components: {
    'member-info-dialog': memberInfoDialog,
    'group-info-dialog': groupInfoDialog,
    'edit-group-dialog': editGroupDialog,
  },
  data: function () {
    return {
      includeInactive: false,
      cid: 0,
      cid_chip: false,
      search: '',
      words: null,
      dataLoaded: false,
      breadcrumbs: [{ text: $T('page.member.breadcrumbs.top') }],
      groups: [],
      members: [],
      showChipFlg: false,
      groupList: [],
      memberList: [],
    }
  },
  mixins: [communitySelect],
  mounted() {
    this.updateData(this.curCid, this.includeInactive)
  },
  watch: {
    curCid(n, o) {
      this.updateData(n, this.includeInactive)
    },
    includeInactive(n, o) {
      this.updateData(this.curCid, n)
    },
    search(n, o) {
      this.words = fmtSearchWord(n)
    },
    cid(newVal, oldVal) {
      this.cid_chip = newVal != 0
    },
    cid_chip(newVal, oldVal) {
      if (!newVal) {
        this.cid = 0
      }
    },
  },
  computed: {
    showChip() {
      return this.showChipFlg && this.cid == 0
    },
    filteredGroups() {
      return this.groups.filter((item) => {
        if (this.cid != 0 && item.community_id != this.cid) {
          return false
        }
        var stw = item.stw
        if (!stw) {
          stw = fmtSearchTgtWord(`${item.name} ${this.getCommunityName(item.community_id)}`)
          item.stw = stw
        }
        return wordMatch(this.words, stw)
      })
    },
    filteredMembers() {
      return this.members.filter((item) => {
        if (this.cid != 0 && item.community_id != this.cid) return false
        var stw = item.stw
        if (!stw) {
          stw = this.toStw(item)
          item.stw = stw
        }
        return wordMatch(this.words, stw)
      })
    },
  },
  methods: {
    toStw(item) {
      return fmtSearchTgtWord(
        `${this.getCmName(item)} ${this.getCommunityName(item.community_id)} ${
          item.status == 1 ? this.$utils.role(item.role) : ''
        } ${item.tag || ''}` // issue#30: タグも検索対象に含める
      )
    },
    updateData(cid, includeInactive) {
      UTILS.startLoading()
      this.showChipFlg = !cid
      var self = this
      if (cid) {
        this.getAllGroupsAndMembers(cid, (r) => {
          self.setGroupsMembersAndAppend(r.gs, r.cms, cid, includeInactive)
        })
        return
      }

      var count = this.stateJoinedCommunities.length
      if (count == 0) {
        this.groups = []
        this.members = []
        return UTILS.stopLoading()
      }

      var loadedCount = 0
      var gs = {}
      var cms = {}
      for (var c of this.stateJoinedCommunities) {
        this.getAllGroupsAndMembers(c.id, (r) => {
          loadedCount++
          gs = Object.assign(gs, r.gs)
          cms = Object.assign(cms, r.cms)
          if (count == loadedCount) {
            self.setGroupsMembersAndAppend(gs, cms, cid, includeInactive)
          }
        })
      }
    },
    onProfileChanged(d) {
      for (var m of this.members) {
        if (d.cmid == m.id) {
          m.cover_changed_at = d.t
          m.nickname = d.name
          m.stw = this.toStw(m)
          return
        }
      }
    },
    // issue#30: re-tag 後にメンバー行の tag を更新（in-memory ＋ clientdb キャッシュ）
    onTagChanged(d) {
      var tgt = null
      for (var m of this.members) {
        if (m.id == d.cmid) {
          tgt = m
          this.$set(m, 'tag', d.tag)
          break
        }
      }
      if (!tgt) return
      tgt.stw = this.toStw(tgt) // issue#30: タグを検索対象に含めるため、再タグ後にキャッシュ済み検索語(stw)を再計算
      var key = `groups-members-${tgt.community_id}`
      var self = this
      this.$commin.clientdb.getObject(key, function (data) {
        if (!data || !data.cms || !data.cms[d.cmid]) return
        data.cms[d.cmid].tag = d.tag
        self.$commin.clientdb.putObject(key, data)
      })
    },
    setGroupsMembersAndAppend(gs, cms, cid, includeInactive) {
      if (!includeInactive) {
        return this.setGroupsMembers(gs, cms, cid)
      }
      var self = this
      this.$utils.getInactiveMembers(cid, (r) => {
        Object.assign(cms, r.cms)
        self.setGroupsMembers(gs, cms, cid)
      })
    },
    setGroupsMembers(gs, cms, cid) {
      var gArr = [],
        cmArr = []
      for (var k of Object.keys(gs)) gArr.push(gs[k])
      for (var k of Object.keys(cms)) cmArr.push(cms[k])
      this.groups = gArr
      this.members = cmArr
      if (cid && this.members.length > 1 && this.cMap[cid].should_invite) {
        this.resetShouldInviteMsg()
      }
      UTILS.stopLoading()
    },
    onRecordClicked(event) {
      var classVals = $(event.target).closest('.c-click').attr('class').split(' ')
      var target = null
      for (var i = 0; i < classVals.length; i++) {
        if (classVals[i].startsWith('c-click-')) {
          target = classVals[i]
          break
        }
      }
      if (!target) {
        console.error('there is no class like [c-click-*]')
        return
      }

      var id = target.substring('c-click-x-'.length)
      if (target.startsWith('c-click-c-')) {
        this.cid = id
        return
      }

      if (target.startsWith('c-click-g-')) {
        for (var g of this.groups) {
          if (g.id == id) return this.$refs.groupInfoDialog.openDialog(g)
        }
        return
      }
      if (target.startsWith('c-click-m-')) {
        var cm = null
        for (var i = 0; i < this.members.length; i++) {
          if (this.members[i].id == id) {
            cm = this.members[i]
            break
          }
        }
        if (!cm) {
          console.error('failed to get member[' + id + ']')
          return
        }

        var c = this.getCommunity(cm.community_id)
        this.$refs.memberInfoDialog.openDialog(cm)
        return
      }
    },
    onCreateGroupClicked() {
      this.$refs.editGroupDialog.openCreateDialog()
    },
    onButtonClicked(key) {
      if (key == 'groups') {
        alert('todo')
        return
      }
      if (key == 'members') {
        this.$router.push({ name: 'memberInvite', params: { id: this.selectedId } })
        return
      }
      console.warn('unknown key ' + key)
    },
    changeValues(cmid, role, status) {
      console.log({ cmid: cmid, role: role, status: status })
      for (var item of this.members) {
        if (item.id == cmid) {
          if (item.is_friend) {
            item.friend_status = status
          } else {
            item.role = role
            item.status = status
          }
          break
        }
      }
    },
    onGroupChanged(group) {
      var gs = []
      var merged = false
      for (var i = 0, n = this.groups.length; i < n; i++) {
        if (this.groups[i].id == group.id) {
          merged = true
          gs.push(group)
        } else {
          gs.push(this.groups[i])
        }
      }
      if (!merged) gs.unshift(group)
      this.groups = gs

      var key = `groups-members-${group.community_id}`
      var self = this
      this.$commin.clientdb.getObject(key, function (data) {
        if (!data) return
        data.gs[group.id] = group
        self.$commin.clientdb.putObject(key, data)
      })
    },
    onGroupDeleted(gid) {
      console.log('deleted ' + gid)
      var gs = []
      var tgtGroup = null
      for (var i = 0, n = this.groups.length; i < n; i++) {
        if (this.groups[i].id == gid) {
          tgtGroup = this.groups[i]
        } else {
          gs.push(this.groups[i])
        }
      }
      this.groups = gs

      if (tgtGroup == null) return console.warn(`there is no group ${gid}`)

      var key = `groups-members-${tgtGroup.community_id}`
      var self = this
      this.$commin.clientdb.getObject(key, function (data) {
        if (!data) return
        delete data.gs[gid]
        self.$commin.clientdb.putObject(key, data)
      })
    },
  },
}
</script>

<style scoped>
.container {
  padding: 0px;
}
.c-subheader.first {
  margin-top: 10px;
}
.c-m-bc {
  font-size: 14px;
}
.c-m-header {
  display: flex;
}
.c-header-space {
  height: 14px;
}
.c-m-search {
}
.c-m-search .v-text-field {
  margin-top: 2px;
}
.c-m-sub-button {
  padding-right: 20px;
}

.c-m-button {
  padding-right: 10px;
}
.c-m-button a {
  margin: 0px;
}
.c-invite-btn {
  margin: -6px 10px 0 0;
  padding: 0px 15px 0 14px;
}

.c-member-invite-btn {
  position: fixed;
  bottom: 90px;
  left: 50%;
  transform: translateX(-50%);
  font-size: 16px !important;
  padding-top: 9px;
  height: 42px;
  min-width: 220px;
}

.c-member-invite-btn i {
  font-size: 20px;
  margin-right: 3px;
}

.c-sub-btn {
  padding: 0;
  margin: 0;
  font-size: 13px;
}

.c-subheader {
  display: flex;
  align-items: center;
  width: 100%;
}
.c-subheader .l {
  flex-grow: 1;
}
.c-subheader .r {
  text-align: right;
}

.v-list {
  padding: 0px 0px 5px 0px;
}
.v-subheader {
  height: 41px;
  padding: 15px 16px 5px 16px;
}
.v-list--two-line .v-list__tile {
  height: 48px !important;
}
.c-m-r {
  position: relative;
}
.c-self-mark {
  position: absolute;
  top: 7px;
  left: 45px;
  z-index: 2;
}
.c-self-mark i {
  filter: drop-shadow(1px 1px 1px rgba(0, 0, 0, 0.6));
}
.c-tip-btn {
  padding-left: 10px;
}
.c-top-search {
}

.le-tbs .c-m-bc {
  position: fixed;
  bottom: 150px;
  left: 0;
  width: 100vw;
  min-width: 320px;
  z-index: 30;
}
.le-tbs .c-top-search {
  min-width: 320px;
  position: fixed;
  top: 48px;
  left: 0;
  width: 100vw;
  background-color: #fff;
  margin-bottom: 5px;
  height: 46px;
  z-index: 50;
}
.le-tbs .c-header-space {
  display: none;
}
.le-tbs .v-list {
  padding: 0;
  border-bottom: 1px solid rgba(0, 0, 0, 0.24);
  border-top: none;
  border-left: none;
  border-right: none;
}
.le-tbs .c-m-r a {
  padding-right: 5px;
}

.c-top-chip {
  display: flex;
  justify-content: flex-start;
  align-items: center;
  margin: 0 6px 0 -5px;
}
.c-top-list-space {
  height: 46px;
}
.c-sub-title {
  height: 18px;
  display: flex;
  align-items: center;
}
/* issue#30: タグは名前の下・左端。役割等はタグの右に並ぶ */
.c-tag-mini {
  display: inline-block;
  vertical-align: middle;
  margin-right: 6px;
  padding: 0 6px;
  border-radius: 9px;
  background-color: #e8f5e9;
  color: #1b5e20;
  font-size: 11px;
  line-height: 16px;
  max-width: 140px;
  overflow: hidden;
  white-space: nowrap;
  text-overflow: ellipsis;
  flex: none;
}
.c-tag-mini i {
  color: #1b5e20 !important;
  margin-right: 2px;
  vertical-align: middle;
}
</style>
